<template>
    <app-layout>
        <template #header>
            <AppHead title="Shipment" />
            <h3 class="title my-2">Shipment</h3>
        </template>

        <div class="py-2">
            <div class="row">
                <div class="col-12 mb-3">
                    <ReportShipping v-if="isMtd()" :notes="notes" />

                    <h2 style="text-align: center;">Shipment # {{ shipment.id }}</h2>
                    <div class="my-2 text-right">
                        <jet-button icon="fas fa-file-invoice-dollar" @click="openPrintReceipt">Print
                            Receipt</jet-button>
                    </div>

                    <div v-if="shipment.status_desc === 'Backorder' && shipment.backorder_eta">
                        <h4>Backorder - Expected to Ship {{ moment.utc(shipment.backorder_eta).format('dddd MMM D, YYYY') }}
                        </h4>
                    </div>
                    <div v-else-if="shipment.status_desc === 'Shipped'">
                        <div v-if="shipment.events.length">
                            <button class="btn btn-outline-primary float-right" @click="showShipmentEvents">Track
                                Package</button>

                            <h4 v-if="shipment.delivery_date">
                                Arriving {{ moment.utc(shipment.delivery_date).format('dddd MMM D, YYYY') }}
                            </h4>
                            <strong>Last Update:</strong> {{ moment(shipment.events[0].event_date).format('M/D/YY h:mm A')
                            }}
                            {{ shipment.events[0].event_description }}
                            {{ shipment.events[0].event_city }}, {{ shipment.events[0].event_state }}
                        </div>
                        <div v-else-if="shipment.shipped_at">
                            <h4>Shipped {{ moment(shipment.shipped_at).format('dddd MMM D, YYYY') }}</h4>
                        </div>
                    </div>
                    <div v-else-if="shipment.status_desc === 'Delivered'">
                        <button v-if="shipment.events.length" class="btn btn-outline-primary float-right"
                            @click="showShipmentEvents">Track Package</button>
                        <h4 v-if="shipment.delivered_at">Delivered {{ moment(shipment.delivered_at).format('MMM D, YYYY') }}
                        </h4>
                    </div>
                    <div v-else>
                        <h4>Prepared {{ moment(shipment.created_at).format('MMM D, YYYY') }}</h4>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col" v-if="shipment.address || shipment.payments.length">
                    <div class="card mb-3" v-if="shipment.address">
                        <div class="card-header border-0">
                            <h3>Shipping To</h3>
                        </div>
                        <div class="card-body">
                            <span v-if="shipment.address.is_customer_direct_destination">{{ shipment.address.name
                            }}<br></span>
                            {{ shipment.address.address1 }} {{ shipment.address.address2 }}<br />
                            {{ shipment.address.city }}, {{ shipment.address.state }} {{ shipment.address.postcode }}
                            <span v-if="shipment.address.is_customer_direct_destination && shipment.address.phone">
                                <br>
                                <Phone v-model="shipment.address.phone" />
                            </span>
                        </div>
                    </div>

                    <div class="card" v-if="shipment.payments.length">
                        <div class="card-header">
                            <h3>Payments</h3>
                        </div>

                        <table class="table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Payment #</th>
                                    <th>Order #</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="payment in shipment.payments">
                                    <td>{{ moment.utc(payment.date).format('M/D/YY') }}</td>
                                    <td>
                                        <inertia-link v-if="payment.payment_id"
                                            @click="openPrintPaymentReceipt(payment.payment_id)">
                                            {{ payment.payment_id }}
                                        </inertia-link>
                                    </td>
                                    <td>
                                        <inertia-link v-if="payment.order" :href="getViewOrderLink(payment.order.id)">
                                            {{ payment.order.id }}
                                        </inertia-link>
                                    </td>
                                    <td>{{ formatDollars(payment.amount) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div class="col">
                    <div class="card">
                        <div class="card-header bg-primary text-white">
                            <h3>Packages in this shipment</h3>
                        </div>

                        <table class="table">
                            <tbody>
                                <template v-for="(box, index) in shipment.packages">
                                    <tr class="bg-light">
                                        <td colspan="4">
                                            <span class="mr-3">Package #{{ index + 1 }}</span>

                                            <template v-if="box.tracking_number">
                                                <a :href="`https://www.fedex.com/fedextrack?trknbr=${box.tracking_number}`"
                                                    target="_blank" class="d-inline-block mr-2"
                                                    v-if="shipment.carrier == 'fedex'">
                                                    FedEx {{ box.tracking_number }}
                                                </a>
                                                <a :href="`http://wwwapps.ups.com/WebTracking/track?track=yes&trackNums=${box.tracking_number}`"
                                                    target="_blank" class="d-inline-block mr-2"
                                                    v-else-if="shipment.carrier == 'ups'">
                                                    UPS {{ box.tracking_number }}
                                                </a>

                                                <span class="badge badge-secondary cursor-pointer pt-1"
                                                    @click="copyTrackingNumber(box.tracking_number)">
                                                    Copy
                                                </span>
                                            </template>
                                        </td>
                                    </tr>

                                    <tr v-for="item in box.items">
                                        <td></td>
                                        <td>{{ item.qty }}</td>
                                        <td>
                                            <inertia-link v-if="item.link_product_id"
                                                :href="route(productRoute, item.link_product_id)" class="text-nowrap">{{
                                                    item.sku }}</inertia-link>
                                            <span v-else class="text-nowrap">{{ item.sku }}</span>
                                        </td>
                                        <td>{{ item.name }}</td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <ShippingEvents :defaultEvents="events" @close="events = null"></ShippingEvents>

        <teleport to="body">
            <PrintModal :printUrl="printUrl" @print="print" />
        </teleport>
    </app-layout>
</template>

<script>
import AppLayout from '@/Layouts/AppLayout'
import moment from 'moment'
import ShippingEvents from '@/Components/Modals/ShippingEvents'
import ReportShipping from '@/Components/Products/ReportShipping'
import PrintModal from '@/Components/Modals/PrintModal'

export default {
    components: {
        AppLayout,
        ShippingEvents,
        ReportShipping,
        PrintModal,
    },
    props: {
        shipment: Object,
        notes: Array,
    },
    data() {
        return {
            printUrl: '',
            events: null,
            productRoute: this.isMtd() ? 'mtd.wholesale.view' : 'products.view',
            selectedReceiptPaymentId: null,
            printPaymentUrl: '',
        }
    },
    created() {
        this.moment = moment
    },
    methods: {
        showShipmentEvents() {
            this.events = this.shipment.events;
        },
        copyTrackingNumber(number) {
            navigator.clipboard.writeText(number);
        },
        getViewOrderLink(orderId) {
            return this.isMtd()
                ? route('mtd.order.view', orderId)
                : route('order.view', orderId);
        },
        openPrintReceipt() {
            this.printReport = 'shipments.receipt'
            this.openPrintDialog();
        },
        openPrintPaymentReceipt(paymentId) {
            this.selectedReceiptPaymentId = paymentId
            this.printReport = 'tu.payment.receipt'
            this.openPrintDialog()
        },
        openPrintDialog() {
            this.setPrintUrl(0);
            $('#PrintModal').modal('show');
        },
        print() {
            this.setPrintUrl(1);
        },
        setPrintUrl(print) {
            switch (this.printReport) {
                case 'tu.payment.receipt':
                    this.printUrl = this.route('tu.payment.receipt', { payment: this.selectedReceiptPaymentId, print: print });
                    break;
                case 'shipments.receipt':
                    this.printUrl = this.route('shipments.receipt', { shipment: this.shipment.id, print: print});
                    break;
            }
        },
    },
}
</script>
